// Centered modal that surfaces a larger product photo + description + SKU
// when the customer clicks an option row's thumbnail. The thumbnail click
// uses stopPropagation so opening the detail view does NOT toggle the row's
// selection — they're separate concerns.
//
// Dismissal: click backdrop, press ESC, click the close button.
// Brand styling: amber accents, dashed dividers, sharp corners, Raleway
// title + Open Sans body.

import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

import type { Product } from "../api";
import {
  descriptionFor,
  displayNameFor,
  formatDollars,
  imageCandidatesFor,
  videoUrlFor,
  videoAutoplayFor,
  videoEmbedUrl,
  localVideoFor,
} from "../format";
import { useConfigurator } from "../store";
import { effectivePriceCents, strikethroughOriginalCents } from "../rules";

interface Props {
  product: Product | null;
  onClose: () => void;
}

export function ProductDetailModal({ product, onClose }: Props) {
  // ESC-to-close. Listener only attached while modal is open.
  useEffect(() => {
    if (!product) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [product, onClose]);

  return (
    <AnimatePresence>
      {product && (
        <motion.div
          key="backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          onClick={onClose}
          className="fixed inset-0 z-50 flex items-center justify-center bg-ink/60 p-4 backdrop-blur-sm"
        >
          <motion.div
            key="card"
            initial={{ opacity: 0, scale: 0.96, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 8 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="product-detail-title"
            className="relative flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-card border border-border bg-white shadow-lg"
          >
            <CloseButton onClick={onClose} />
            <Body product={product} />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function CloseButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Close"
      className="absolute right-3 top-3 z-10 flex h-9 w-9 items-center justify-center rounded-full border border-line bg-white text-ink shadow-sm transition hover:border-ink"
    >
      <svg
        viewBox="0 0 16 16"
        className="h-4 w-4"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="square"
      >
        <path d="M3 3 L13 13 M13 3 L3 13" />
      </svg>
    </button>
  );
}

function Body({ product }: { product: Product }) {
  const { state } = useConfigurator();
  const name = displayNameFor(product);
  const description = descriptionFor(product);
  const candidates = imageCandidatesFor(product);
  const videoUrl = videoUrlFor(product);
  const videoAutoplay = videoAutoplayFor(product);
  const localVideo = localVideoFor(product);

  // Price mirrors the builder rows: apply the same effective-price + strike-
  // through override (e.g. Accelerate's −$500 when a machine is selected) so
  // the detail card and the row never disagree.
  const ctx = state.catalog
    ? {
        catalog: state.catalog,
        selectedMachineId: state.selectedMachineId,
        selectedAccessoryIds: state.selectedAccessoryIds,
        selectedTableId: state.selectedTableId,
        existingSystemAnswers: state.existingSystemAnswers,
        preloved: Boolean(state.preloved),
        prelovedCategory: state.preloved?.category ?? null,
      }
    : null;
  const priceCents = ctx
    ? effectivePriceCents(product.id, product.price_cents, ctx)
    : product.price_cents;
  const strikeCents = ctx
    ? strikethroughOriginalCents(product.id, product.price_cents, ctx)
    : null;

  return (
    <div className="flex flex-1 flex-col overflow-y-auto">
      {/* Media — a self-hosted looping video first, then a Vimeo/YouTube embed,
          else the image (which falls back to hatch + monogram). */}
      {localVideo ? (
        <LocalVideo sources={localVideo} title={name} />
      ) : videoUrl ? (
        <VideoEmbed url={videoUrl} title={name} autoplay={videoAutoplay} />
      ) : (
        <DetailImage candidates={candidates} alt={name} />
      )}

      <div className="flex flex-col gap-4 p-6 sm:p-8">
        <div className="flex flex-col gap-1 border-b border-dashed border-line pb-4">
          <div className="flex items-center gap-2">
            <span aria-hidden className="h-2 w-2 bg-amber" />
            <span className="font-sans text-[14px] font-bold uppercase tracking-tag text-steel">
              Product Detail
            </span>
          </div>
          <div className="flex items-baseline justify-between gap-4">
            <h3
              id="product-detail-title"
              className="font-display text-2xl font-extrabold uppercase leading-tight text-ink"
            >
              {name}
            </h3>
            <span className="flex items-baseline gap-2 whitespace-nowrap font-display text-2xl font-extrabold text-ink">
              {strikeCents != null && (
                <span className="font-sans text-base font-normal text-steel line-through">
                  {formatDollars(strikeCents)}
                </span>
              )}
              <span>{formatDollars(priceCents)}</span>
            </span>
          </div>
          {product.sku && (
            <p className="font-sans text-[14px] uppercase tracking-tag text-steel">
              SKU: <span className="font-mono">{product.sku}</span>
            </p>
          )}
        </div>

        {description ? (
          <div
            className="item-desc font-sans text-sm leading-relaxed text-ink"
            dangerouslySetInnerHTML={{ __html: description }}
          />
        ) : (
          <p className="font-sans text-sm italic text-steel">
            No description yet — your Gammill representative can tell you more.
          </p>
        )}
      </div>
    </div>
  );
}

function LocalVideo({
  sources,
  title,
}: {
  sources: { webm: string; mp4: string; poster: string };
  title: string;
}) {
  // Self-hosted B-roll: autoplay muted + loop (the only autoplay browsers allow),
  // playsInline so iOS doesn't force fullscreen. Poster shows until it paints;
  // webm is offered first (smaller), mp4 as the universal fallback.
  return (
    <video
      className="aspect-video w-full bg-ink object-cover"
      poster={sources.poster}
      autoPlay
      muted
      loop
      playsInline
      preload="metadata"
      aria-label={title}
    >
      <source src={sources.webm} type="video/webm" />
      <source src={sources.mp4} type="video/mp4" />
    </video>
  );
}

function VideoEmbed({
  url,
  title,
  autoplay = false,
}: {
  url: string;
  title: string;
  autoplay?: boolean;
}) {
  const embed = videoEmbedUrl(url, autoplay);
  if (!embed) return null;
  // The player sits "in place of the image" in a 16:9 slot. Without autoplay it
  // shows the poster + native play button; with autoplay it starts muted.
  return (
    <div className="relative aspect-video w-full bg-ink">
      <iframe
        src={embed}
        title={title}
        loading="lazy"
        allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
        allowFullScreen
        className="absolute inset-0 h-full w-full"
      />
    </div>
  );
}

function DetailImage({ candidates, alt }: { candidates: string[]; alt: string }) {
  const [idx, setIdx] = useState(0);
  // Track a successful load so the hatch + name placeholder is shown ONLY
  // when no image renders. Otherwise a transparent/letterboxed photo lets
  // the placeholder peek through around its edges.
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    setIdx(0);
    setLoaded(false);
  }, [candidates.join("|")]);

  const url = candidates[idx];

  return (
    <div className="relative aspect-[4/3] w-full bg-mist">
      {!loaded && (
        <>
          <div
            aria-hidden
            className="absolute inset-0 opacity-30"
            style={{
              backgroundImage:
                "repeating-linear-gradient(45deg, transparent 0 20px, #E4E4E4 20px 21px)",
            }}
          />
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="select-none px-6 text-center font-display text-3xl font-extrabold uppercase tracking-tight text-steel sm:text-5xl">
              {alt}
            </span>
          </div>
        </>
      )}
      {url && (
        <img
          key={url}
          src={url}
          alt={alt}
          onLoad={() => setLoaded(true)}
          onError={() => {
            setLoaded(false);
            setIdx((i) => i + 1);
          }}
          className="absolute inset-0 h-full w-full object-contain p-6"
        />
      )}
    </div>
  );
}
